import { useState, useEffect } from "react";
import { Trophy, Users, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { getLeaderboardStatsAPI } from "../../api/miningApi";

export default function MiningLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("allTimeReferral");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getLeaderboardStatsAPI();
      if (response.success) {
        setData(response.data);
      } else {
        setError("Failed to fetch leaderboard");
      }
    } catch (err) {
      setError("Failed to fetch leaderboard");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    return `${hours}h`;
  };

  const tabs = [
    { key: "allTimeReferral", label: "All Time Referrals", icon: Users },
    { key: "weeklyReferral", label: "Weekly Referrals", icon: Users },
    { key: "allTimeMining", label: "All Time Mining", icon: Clock },
    { key: "weeklyMining", label: "Weekly Mining", icon: Clock },
  ];

  const currentData = data?.[activeTab] || [];
  const totalPages = Math.ceil(currentData.length / pageSize);
  const paginatedData = currentData.slice((page - 1) * pageSize, page * pageSize);
  const isReferral = activeTab.includes("Referral");

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 text-base mb-4">{error}</p>
          <button
            onClick={fetchLeaderboard}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground"><span className="text-gradient-ginox">Leaderboard</span></h1>
        <p className="text-sm text-muted-foreground">Top performers</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-medium transition-colors ${
              activeTab === tab.key
                ? "border border-ginox-green/60 bg-ginox-green/10 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <div className="border-b border-border/70 px-5 py-3.5">
          <p className="text-sm font-medium text-foreground">
            {tabs.find((t) => t.key === activeTab)?.label}
          </p>
        </div>
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {["Rank", "User ID", isReferral ? "Total Referrals" : "Total Duration"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-12 text-center text-sm text-muted-foreground">No data</td>
              </tr>
            ) : (
              paginatedData.map((item, idx) => {
                const rank = (page - 1) * pageSize + idx + 1;
                return (
                  <tr key={item.userId} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono font-bold">
                      {rank === 1 ? (
                        <span className="text-yellow-400">#{rank}</span>
                      ) : rank === 2 ? (
                        <span className="text-zinc-400">#{rank}</span>
                      ) : rank === 3 ? (
                        <span className="text-amber-600">#{rank}</span>
                      ) : (
                        <span className="text-muted-foreground">#{rank}</span>
                      )}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{item.userId}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      {isReferral ? item.totalReferrals : formatDuration(item.totalDuration)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, currentData.length)} of {currentData.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = i + 1;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
                    p === page
                      ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                      : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
